import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule, TranslateParser, TranslateService } from '@ngx-translate/core';

import {
    MaterialAlertComponent,
    MaterialItemListComponent,
    MaterialSpinnerComponent,
    PhoneNumberUtil,
} from '@ecm/material';
import { Contract, ContractHelper, ContractsBusinessModel, PortfolioService } from '@ecm/portfolio';
import { KenobiService, Status, TMSService } from '@ecm/tracker';

import { ErrorPageService } from '../../../services/error-page.service';
import { RouterService } from '../../../services/router.service';
import { TitleService } from '../../../services/title.service';
import { AbstractSelectorComponent } from '../abstract-selector.component';
import { SelectorReboundLinksComponent } from '../shared/rebound-links/selector-rebound-links.component';
import { SelectorIdTrackingEnum } from '../shared/selector-id-tracking.enum';
import { SelectorUniverseEnum } from '../shared/selector-universe.enum';

@Component({
    selector: 'ecm-selector-contract',
    templateUrl: 'selector-contract.html',
    standalone: true,
    imports: [
        RouterLink,
        NgClass,
        MaterialItemListComponent,
        MaterialAlertComponent,
        SelectorReboundLinksComponent,
        MaterialSpinnerComponent,
        TranslateModule,
    ],
})
export class SelectorContractComponent extends AbstractSelectorComponent implements OnInit {
    public contractDescriptions: Map<string, string[]>;

    public constructor(
        protected activatedRoute: ActivatedRoute,
        protected errorPageService: ErrorPageService,
        protected routerService: RouterService,
        protected portfolioService: PortfolioService,
        protected kenobiService: KenobiService,
        protected tmsService: TMSService,
        private translateService: TranslateService,
        private translateParser: TranslateParser,
        private readonly titleService: TitleService,
    ) {
        super(activatedRoute, errorPageService, routerService, portfolioService, kenobiService, tmsService);

        translateService.use('i18n');

        this.universe = SelectorUniverseEnum.CONTRACT;
        this.idTracking = SelectorIdTrackingEnum.CONTRACT;
    }

    /**
     * @inheritDoc
     */
    public ngOnInit() {
        super.init();
        const filters = this.activatedRoute.snapshot.queryParams;
        const contractsFilter = filters.filter || filters.verticale;
        this.titleService.setTitle(this.translateService.instant('ecm.selector.contract.title'), false, '');
        this.portfolioService.getRawContracts(contractsFilter).subscribe({
            next: (contracts: ContractsBusinessModel) => {
                const availableContracts = ContractHelper.applyFilters(contracts, filters);

                this.computeContractDescriptions(availableContracts);
                const hasLineNumber = this.activatedRoute.snapshot.queryParams.returnUrl.includes('lineNumber=');
                if (availableContracts.length === 1 && !hasLineNumber && !filters.force) {
                    // only one contract is available
                    this.redirectToReturnUrl(availableContracts[0]);
                } else {
                    this.groupContractsByBrand(availableContracts);

                    // Tracking
                    this.onAfterViewLoaded(Status.OK, 'CTRMAF');
                }
            },
            error: () => {
                // Tracking
                this.onAfterViewLoaded(Status.KORG, 'CTRMAF');

                this.errorPageService.showDefaultErrorPage();
            },
        });
    }

    public getContractActivity(contract: Contract) {
        return ['ACTIF', 'EN CREATION'].indexOf(contract.status) !== -1 ? 'actif' : 'clos';
    }

    private computeContractDescriptions(contracts: Contract[]): void {
        this.contractDescriptions = new Map<string, string[]>();
        contracts.forEach((contract) =>
            this.contractDescriptions.set(contract.cid, this.getContractDescription(contract)),
        );
    }

    private getContractDescription(contract: Contract) {
        const translation = this.translateService.instant(
            `ecm.selector.contract.${contract.universe}.${this.getContractActivity(contract)}.desc`,
        );
        const description = typeof translation === 'string' ? [translation] : translation;
        return description.map((str) =>
            this.translateParser.interpolate(str, {
                lineNumber: PhoneNumberUtil.getFormattedPhone(contract.telco?.publicNumber),
                offerName: contract.offerName,
                cityName: contract.contractInstallationArea?.city ?? '',
            }),
        );
    }
}
